/**
 * Kanban card: number, table, age, items, total, main action, secondary menu.
 * docs/15, section 4.2.
 *
 * The actions come from the state machine (ORDER_TRANSITIONS): a forbidden
 * action is absent, never greyed out.
 */

import { useRef, useState } from 'react';
import type { OrderAction, OrderStatus, OrderSummaryDTO } from '@scan2eat/shared';
import { formatMoney, ORDER_TRANSITIONS } from '@scan2eat/shared';

import { ageMinutes, formatAge } from '../lib/board.js';
import { labels } from '../labels.js';
import { CancelDialog } from './CancelDialog.js';
import { DropdownItem, DropdownMenu } from './DropdownMenu.js';
import { Icon } from './Icon.js';
import styles from './OrderCard.module.css';

/** Minutes after which a card in NEW or IN_PROGRESS asks for attention. docs/15, section 4.2. */
export const URGENCY = { warning: 10, critical: 20 } as const;

function actionsFor(status: OrderStatus): OrderAction[] {
  return Object.keys(ORDER_TRANSITIONS[status]) as OrderAction[];
}

interface OrderCardProps {
  order: OrderSummaryDTO;
  now: number;
  flashing: boolean;
  /** Only NEW and IN_PROGRESS carry the age alert. */
  watchAge: boolean;
  isDone: boolean;
  onOpenDetail: () => void;
  onTransition: (action: OrderAction, note?: string) => void;
  onVerifyPayment: (decision: 'CONFIRM' | 'REJECT') => void;
}

export function OrderCard({ order, now, flashing, watchAge, isDone, onOpenDetail, onTransition, onVerifyPayment }: OrderCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);
  const actions = actionsFor(order.status);

  // Encaisser n'est pas une decision du personnel : c'est le convive qui
  // choisit son moyen de paiement depuis son telephone, et le personnel qui
  // le constate. Tant qu'il n'a rien declare, la commande n'est pas
  // encaissable. Voir docs/06, section 10.
  const declaredPayment = order.payment?.status === 'DECLARED' ? order.payment : null;
  const awaitingDeclaration = order.status === 'SERVED' && !declaredPayment;

  // Confirmer un paiement declare **est** l'encaissement : la barre de
  // paiement prend la main sur l'action principale.
  const primary = awaitingDeclaration || declaredPayment ? undefined : actions.find((a) => a !== 'CANCEL');
  const secondary = actions.filter((a) => a !== primary && a !== 'CANCEL' && a !== 'COMPLETE');
  const canCancel = actions.includes('CANCEL');

  const minutes = ageMinutes(order.placedAt, now);
  const urgency = !watchAge
    ? 'neutral'
    : minutes >= URGENCY.critical ? 'critical' : minutes >= URGENCY.warning ? 'warning' : 'neutral';

  // Defensif : une charge utile inattendue doit degrader l'affichage, jamais
  // faire tomber le kanban au milieu d'un service.
  const preview = order.itemsPreview ?? [];
  const previewQuantity = preview.reduce((sum, line) => sum + line.quantity, 0);
  const moreItems = Math.max(0, (order.itemCount ?? previewQuantity) - previewQuantity);

  const time = (iso: string | null) =>
    iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';

  const cardClass = [
    styles.card,
    flashing ? 'card-new' : '',
    isDone ? styles.done : '',
    urgency === 'warning' ? styles.warning : '',
    urgency === 'critical' ? styles.critical : '',
  ].join(' ');

  return (
    <>
      <article className={cardClass} aria-label={labels.order.number(order.number)}>
        <header className={styles.head}>
          <button type="button" className={styles.open} onClick={onOpenDetail}>
            <span className={styles.number}>#{order.number}</span>
            <span className={styles.table}>{labels.app.tableLabel(order.table.label)}</span>
          </button>

          {isDone ? (
            <span className={styles.doneAt}>
              <Icon name="check" size={14} />
              {time(order.completedAt ?? order.placedAt)}
            </span>
          ) : (
            // Lecture seule : placedAt determine le jour de service, le chiffre
            // d'affaires et le temps de preparation du bilan.
            <span className={`${styles.age} ${styles[`age_${urgency}`]}`} title={labels.board.age}>
              <Icon name="clock" size={14} />
              <span className="tabular">{formatAge(order.placedAt, now)}</span>
            </span>
          )}

          {!isDone && (
            <div className={styles.menuWrap}>
              <button
                ref={menuRef}
                type="button"
                className="btn-icon btn-icon--quiet btn-icon--sm"
                aria-label={labels.board.moreActions}
                aria-expanded={menuOpen}
                aria-haspopup="true"
                onClick={() => setMenuOpen((v) => !v)}
              >
                <Icon name="more" size={18} />
              </button>
              <DropdownMenu open={menuOpen} onClose={() => setMenuOpen(false)} anchorRef={menuRef}>
                <DropdownItem onClick={() => { setMenuOpen(false); onOpenDetail(); }} icon={<Icon name="receipt" size={18} />}>
                  {labels.detail.title}
                </DropdownItem>
                {secondary.map((action) => (
                  <DropdownItem
                    key={action}
                    onClick={() => { setMenuOpen(false); onTransition(action); }}
                    icon={<Icon name={action === 'HOLD' ? 'clock' : 'arrow-right'} size={18} />}
                  >
                    {labels.actions[action]}
                  </DropdownItem>
                ))}
                {canCancel && (
                  <>
                    <div className="dropdown-separator" />
                    <DropdownItem onClick={() => { setMenuOpen(false); setCancelOpen(true); }} icon={<Icon name="close" size={18} />} danger>
                      {labels.actions.CANCEL}
                    </DropdownItem>
                  </>
                )}
              </DropdownMenu>
            </div>
          )}
        </header>

        {/* ON_HOLD n'a plus de colonne : la pastille porte l'information. */}
        {order.status === 'ON_HOLD' && (
          <span className={`badge badge--dot badge--hold ${styles.hold}`}>{labels.payment.onHold}</span>
        )}

        {!isDone && (
          <ul className={styles.items}>
            {preview.map((line, i) => (
              <li key={`${line.nameSnapshot}-${i}`}>
                <span className={styles.qty}>{line.quantity}</span>
                <span className="truncate">{line.nameSnapshot}</span>
              </li>
            ))}
            {moreItems > 0 && <li className={styles.more}>{labels.board.moreItems(moreItems)}</li>}
          </ul>
        )}

        {/* Paiement annonce par le convive, en attente de verification.
            Confirmer termine la commande et emet la facture.
            Voir docs/06, section 10. */}
        {declaredPayment && (
          <div className={styles.payment}>
            <div className={styles.paymentHead}>
              <Icon name={declaredPayment.method === 'COUNTER' ? 'cash' : 'phone'} size={16} />
              <strong>
                {declaredPayment.method === 'COUNTER'
                  ? labels.payment.counter
                  : labels.payment.providerLabels[declaredPayment.provider ?? 'MTN']}
              </strong>
              {/* Pendant un encaissement chez le prestataire, il n'y a rien a
                  verifier : c'est lui qui tranchera. */}
              {!declaredPayment.awaitingProvider && (
                <span className={styles.paymentTag}>{labels.payment.toVerify}</span>
              )}
            </div>
            {/* Le numero du payeur est ce qui permet de retrouver la
                transaction chez l'operateur quand un convive reclame. La
                reference n'existe que si l'operateur en a rendu une. */}
            {declaredPayment.payerPhone && (
              <p className={styles.reference}>
                {labels.payment.payerPhone} <span className="mono">{declaredPayment.payerPhone}</span>
              </p>
            )}
            {declaredPayment.reference && (
              <p className={styles.reference}>
                {labels.payment.reference} <span className="mono">{declaredPayment.reference}</span>
              </p>
            )}
            {declaredPayment.awaitingProvider ? (
              // Encaissement en cours chez le prestataire : il tranchera, et la
              // carte passera seule en « terminee ». Proposer « Confirmer » ici
              // inviterait a solder une commande dont l'argent n'est pas arrive.
              <p className={styles.reference}>{labels.payment.awaitingProvider}</p>
            ) : (
              <div className={styles.paymentActions}>
                {/* Le libelle dit le geste reel : on encaisse des especes au
                    comptoir, on confirme un paiement mobile constate a la main. */}
                <button type="button" className="btn-primary btn-sm" onClick={() => onVerifyPayment('CONFIRM')}>
                  <Icon name="check" size={16} />
                  {declaredPayment.method === 'COUNTER' ? labels.payment.cashIn : labels.payment.confirm}
                </button>
                <button type="button" className="btn-ghost btn-sm" onClick={() => onVerifyPayment('REJECT')}>
                  {labels.payment.reject}
                </button>
              </div>
            )}
          </div>
        )}

        <footer className={styles.foot}>
          <span className={styles.total}>{formatMoney(order.totalMinor, order.currency)}</span>
          {isDone ? (
            <span className={styles.itemCount}>{labels.board.itemCount(order.itemCount)}</span>
          ) : awaitingDeclaration ? (
            <span className={styles.awaiting}>
              <Icon name="clock" size={14} />
              {order.payment?.status === 'REJECTED'
                ? labels.payment.awaitingNewDeclaration
                : labels.payment.awaitingCustomer}
            </span>
          ) : primary ? (
            <button type="button" className="btn-primary btn-sm" onClick={() => onTransition(primary)}>
              {labels.actions[primary]}
              <Icon name="arrow-right" size={16} />
            </button>
          ) : null}
        </footer>
      </article>

      {cancelOpen && (
        <CancelDialog
          onConfirm={(note) => { setCancelOpen(false); onTransition('CANCEL', note); }}
          onCancel={() => setCancelOpen(false)}
        />
      )}
    </>
  );
}
